'use client';

import { FC, useContext } from 'react';
import { Flex, Toggle } from '@/_components';
import { UserSettingsFormProps } from './UserSettingsForm.types';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { updateUser } from '@/_api/users';

export const UserSettingsForm: FC<UserSettingsFormProps> = ({
  isLoading,
  onOpen,
  onOpenActivateUser,
  onToggleDeactivateAccount,
  refresh,
  setLoading,
}) => {
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { selectedUser, selectedUserPrimaryCompanies } =
    useContext(CompanyContext);

  const isPrimaryOwnerOfAtLeastOneInactiveCompany: boolean =
    selectedUserPrimaryCompanies?.some((company) => company.inactive);

  const onToggleReadOnlyAccount = async (
    _event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!selectedUser) return;

    try {
      setLoading(true);

      const response = await updateUser({
        token,
        userId: selectedUser.id!,
        readonly_access: !selectedUser.readonly_access,
      });

      refresh(response);

      if (response?.error) {
        return addToast('error', 'error updating user', {
          error: response.error,
        });
      }
    } catch (error) {
      addToast('error', 'error updating user', { error });
    } finally {
      setLoading(false);
    }
  };

  const handleActivation = () => {
    if (isPrimaryOwnerOfAtLeastOneInactiveCompany) {
      return onOpenActivateUser();
    }

    return onToggleDeactivateAccount(true);
  };

  return (
    <>
      <Flex isColumn={true} gap={16}>
        <Flex isColumn={true} gap={4}>
          <Toggle
            label="Deactivate Account"
            isChecked={!selectedUser?.paying}
            isDisabled={isLoading}
            onChange={selectedUser?.paying ? onOpen : handleActivation}
            helperText="Its user will not be able to log in or receive notifications. The user will no longer have access to any entities."
          />
        </Flex>
        <Flex isColumn={true} gap={4}>
          <Toggle
            label="Read-Only Account"
            isChecked={!!selectedUser?.readonly_access}
            isDisabled={isLoading || !selectedUser?.paying}
            onChange={onToggleReadOnlyAccount}
            helperText="Its user can still log in to access existing documents, but they will not be able to upload new documents, complete questionnaires, edit profile or send messages in any entities."
          />
        </Flex>
      </Flex>
    </>
  );
};
